import { type Cell, type Header, type HeaderGroup, type Row, Table, flexRender } from '@tanstack/react-table';
import { XIcon } from 'lucide-react';
import * as React from 'react';

import { TableEmptyState, type TableEmptyStateProps } from '@/components/BaseTable/TableEmptyState';
import { TableBody, TableCell, TableHead, TableHeader, TableRow, Table as UITable } from '@/components/ui/table';
import type { TableVariant } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { pluralize } from '@/utils/pluralize';

import { Pagination } from './Pagination';
import { SkeletonLoader } from './SkeletonLoader';

export const DEFAULT_PAGE_SIZE = 30;

export const DEFAULT_ROLE_COLUMN_SIZE = 195;
export const DEFAULT_ACTION_COLUMN_SIZE = 72;

export interface BaseTableProps<TData> {
  table: Table<TData>;
  isLoading: boolean;
  data: TData[];
  toolbarContent?: (table: Table<TData>, data: TData[]) => React.ReactNode;
  extraContent?: (data: TData[]) => React.ReactNode;
  dataAttributes?: {
    'data-cy'?: string;
  };
  emptyStateHeading?: TableEmptyStateProps['heading'];
  emptyStateSubHeading?: TableEmptyStateProps['subHeading'];
  emptyStateActionText?: TableEmptyStateProps['actionText'];
  emptyStateActionIcon?: TableEmptyStateProps['actionIcon'];
  emptyStateActionTo?: TableEmptyStateProps['actionTo'];
  emptyStateActionParams?: TableEmptyStateProps['actionParams'];
  emptyStateActionSearch?: TableEmptyStateProps['actionSearch'];
  emptyStateActionOnClick?: TableEmptyStateProps['actionOnClick'];
  emptyStateActionButtonVariant?: TableEmptyStateProps['actionButtonVariant'];
  variant?: TableVariant;
  customLoadingComponent?: React.ReactNode;
  emptyStateClassName?: TableEmptyStateProps['className'];
  total?: number;
  className?: string;
  fixed?: boolean;
  numberOfFiltersToClear?: number;
  hidePagination?: boolean;
}

export function BaseTable<TData>({
  emptyStateActionText,
  emptyStateActionIcon,
  emptyStateActionTo,
  emptyStateActionParams,
  emptyStateActionSearch,
  emptyStateActionOnClick,
  emptyStateActionButtonVariant,
  data,
  dataAttributes,
  extraContent,
  emptyStateHeading,
  isLoading,
  emptyStateSubHeading,
  table,
  toolbarContent,
  variant,
  customLoadingComponent,
  emptyStateClassName,
  total,
  className,
  fixed,
  hidePagination,
  numberOfFiltersToClear,
}: BaseTableProps<TData>) {
  const renderedToolbar = toolbarContent?.(table, data);
  const renderedExtraContent = extraContent?.(data);
  if (isLoading) {
    return (
      <div className="space-y-4 pb-6">
        {customLoadingComponent || <SkeletonLoader variant={variant} columns={table.getAllColumns().length} />}
      </div>
    );
  }

  // Auto-configure empty state action props when numberOfFiltersToClear > 0
  // Explicitly passed emptyState props take precedence over auto-calculated values
  const hasFiltersToClear = numberOfFiltersToClear && numberOfFiltersToClear > 0;
  const finalEmptyStateActionText =
    emptyStateActionText ??
    (hasFiltersToClear ? `Clear ${numberOfFiltersToClear} ${pluralize(numberOfFiltersToClear, 'filter')}` : undefined);
  const finalEmptyStateActionTo = emptyStateActionTo ?? (hasFiltersToClear ? '.' : undefined);
  const finalEmptyStateActionButtonVariant =
    emptyStateActionButtonVariant ?? (hasFiltersToClear ? 'outline' : undefined);
  const finalEmptyStateActionIcon =
    emptyStateActionIcon ?? (hasFiltersToClear ? <XIcon className="ml-2 h-4 w-4" /> : undefined);

  return (
    <div className="space-y-4 pb-6" {...dataAttributes}>
      {renderedToolbar}
      <div className={cn('rounded-md', variant !== 'ghost' && 'border')}>
        <div className="overflow-x-auto">
          <UITable variant={variant} className={className} fixed={fixed}>
            <TableHeader>
              <TableRow>
                {table.getHeaderGroups().map((headerGroup: HeaderGroup<TData>) => (
                  <React.Fragment key={headerGroup.id}>
                    {headerGroup.headers.map((header: Header<TData, unknown>) => (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{
                          width: `${header.column.getSize()}px`,
                          minWidth: `${header.column.getSize()}px`,
                        }}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </React.Fragment>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row: Row<TData>) => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                    {row.getVisibleCells().map((cell: Cell<TData, unknown>) => (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: `${cell.column.getSize()}px`,
                          minWidth: `${cell.column.getSize()}px`,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={table.getAllColumns().length} className="h-36 p-0">
                    <TableEmptyState
                      heading={emptyStateHeading}
                      subHeading={emptyStateSubHeading || 'No results.'}
                      actionText={finalEmptyStateActionText}
                      actionIcon={finalEmptyStateActionIcon}
                      actionTo={finalEmptyStateActionTo}
                      actionParams={emptyStateActionParams}
                      actionSearch={emptyStateActionSearch}
                      actionOnClick={emptyStateActionOnClick}
                      actionButtonVariant={finalEmptyStateActionButtonVariant}
                      className={emptyStateClassName}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </UITable>
        </div>
      </div>
      {!isLoading && !hidePagination && <Pagination table={table} total={total} />}
      {renderedExtraContent}
    </div>
  );
}
